import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Checkbox,
  ConfirmDialog,
  Field,
  Select,
  Skeleton,
  TextArea,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { prettyJson } from '../lib/format';

interface OperationRow {
  id: string;
  operationKey: string;
  isEnabled: boolean;
  defaultVersion: { version: string } | null;
  versions: Array<{ version: string; state: string; sideEffectClass: string }>;
}

export function TestConsolePage({ role }: { role: string }) {
  const operations = useResource<{ items: OperationRow[] }>('/api/admin/operations');
  const aliases = useResource<{ items: Array<{ alias: string }> }>('/api/admin/model-aliases');
  const toast = useToast();

  const [operationKey, setOperationKey] = useState('');
  const [version, setVersion] = useState('');
  const [environment, setEnvironment] = useState('test');
  const [modelAlias, setModelAlias] = useState('');
  const [input, setInput] = useState('{}');
  const [dryRun, setDryRun] = useState(false);
  const [allowDraft, setAllowDraft] = useState(false);
  const [result, setResult] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [confirmProduction, setConfirmProduction] = useState(false);

  const canOperate = ['OWNER', 'ADMIN', 'OPERATOR'].includes(role);
  const canProduction = ['OWNER', 'ADMIN'].includes(role);
  const selected = (operations.data?.items ?? []).find(
    (operation) => operation.operationKey === operationKey,
  );
  const selectedVersion = selected?.versions.find(
    (candidate) => candidate.version === (version || selected.defaultVersion?.version),
  );

  const invoke = async (productionConfirmed = false) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(input);
    } catch {
      toast.error('Nieprawidłowy JSON', 'Popraw pole danych wejściowych.');
      return;
    }
    setBusy(true);
    try {
      const response = await api.post('/api/admin/test-console/invoke', {
        operationKey,
        ...(version ? { operationVersion: version } : {}),
        input: parsed,
        environment,
        ...(modelAlias ? { modelAlias } : {}),
        dryRun,
        allowDraft,
        ...(productionConfirmed ? { confirmProduction: 'WYKONAJ NA PRODUKCJI' } : {}),
      });
      setResult(response);
      toast.success('Wywołanie zakończone');
    } catch (error) {
      const described = describeError(error);
      setResult({ error: described });
      toast.error(described.title, described.body);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Konsola testowa"
        description="Wywołaj operację bez ujawniania poświadczeń upstreamu. Każde wywołanie jest audytowane i widoczne w dzienniku żądań."
      />

      {!canOperate && (
        <Callout tone="warn">
          Twoja rola pozwala tylko na podgląd. Wywołania wymagają roli OPERATOR lub wyższej.
        </Callout>
      )}

      <div className="ah-grid cols-2">
        <Card title="Żądanie">
          {operations.loading ? (
            <Skeleton rows={5} />
          ) : (
            <>
              <Field label="Operacja">
                {(id) => (
                  <Select
                    id={id}
                    value={operationKey}
                    onChange={(event) => {
                      setOperationKey(event.target.value);
                      setVersion('');
                      setResult(null);
                    }}
                  >
                    <option value="">— wybierz —</option>
                    {(operations.data?.items ?? []).map((operation) => (
                      <option key={operation.id} value={operation.operationKey}>
                        {operation.operationKey}
                        {operation.isEnabled ? '' : ' (wyłączona)'}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {selected && (
                <Field label="Wersja" hint="Puste = wersja domyślna operacji.">
                  {(id) => (
                    <Select
                      id={id}
                      value={version}
                      onChange={(event) => setVersion(event.target.value)}
                    >
                      <option value="">
                        domyślna{' '}
                        {selected.defaultVersion
                          ? `(${selected.defaultVersion.version})`
                          : '(brak — wybierz jawnie)'}
                      </option>
                      {selected.versions.map((candidate) => (
                        <option key={candidate.version} value={candidate.version}>
                          {candidate.version} — {candidate.state}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              )}

              <div className="ah-grid cols-2">
                <Field label="Środowisko">
                  {(id) => (
                    <Select
                      id={id}
                      value={environment}
                      onChange={(event) => setEnvironment(event.target.value)}
                    >
                      <option value="test">test</option>
                      <option value="staging">staging</option>
                      <option value="production">production</option>
                    </Select>
                  )}
                </Field>
                <Field label="Alias modelu" hint="Tylko dla operacji LLM.">
                  {(id) => (
                    <Select
                      id={id}
                      value={modelAlias}
                      onChange={(event) => setModelAlias(event.target.value)}
                    >
                      <option value="">— brak —</option>
                      {(aliases.data?.items ?? []).map((alias) => (
                        <option key={alias.alias} value={alias.alias}>
                          {alias.alias}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>

              <Field label="Dane wejściowe (JSON)">
                {(id) => (
                  <TextArea
                    id={id}
                    rows={10}
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                  />
                )}
              </Field>

              <Checkbox
                label="Tryb dryRun (bez wywołania upstreamu)"
                checked={dryRun}
                onChange={setDryRun}
              />
              <Checkbox
                label="Pozwól na wersję roboczą (DRAFT)"
                hint="Do testowania operacji przed publikacją. Dostępne wyłącznie w konsoli administratora."
                checked={allowDraft}
                onChange={setAllowDraft}
              />

              {environment === 'production' && (
                <Callout tone="danger">
                  Wywołanie produkcyjne wykona prawdziwe żądanie u dostawcy i może wygenerować
                  koszt. Wymaga dodatkowego potwierdzenia.
                </Callout>
              )}
              {selectedVersion && selectedVersion.sideEffectClass !== 'READ' && (
                <Callout tone="warn">
                  Ta operacja jest sklasyfikowana jako{' '}
                  <Badge tone="danger">{selectedVersion.sideEffectClass}</Badge>. Jeśli wymaga
                  potwierdzenia, konsola zwróci CONFIRMATION_REQUIRED — to zamierzone zachowanie.
                </Callout>
              )}

              <Button
                variant="primary"
                disabled={!canOperate || !operationKey || busy}
                onClick={() => {
                  if (environment === 'production') setConfirmProduction(true);
                  else void invoke(false);
                }}
              >
                {busy ? 'Wywołuję…' : 'Wywołaj'}
              </Button>
            </>
          )}
        </Card>

        <Card title="Odpowiedź">
          {result === null ? (
            <p style={{ color: 'var(--ah-text-muted)' }}>Wynik pojawi się tutaj.</p>
          ) : (
            <pre className="ah-code">{prettyJson(result)}</pre>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={confirmProduction}
        onOpenChange={setConfirmProduction}
        title="Wywołanie na produkcji"
        description="To wywołanie użyje produkcyjnego konta dostawcy, wykona prawdziwe żądanie i może wygenerować koszt. Zostanie zapisane w dzienniku audytu."
        confirmLabel="Wykonaj"
        destructive
        confirmPhrase="WYKONAJ NA PRODUKCJI"
        onConfirm={() => invoke(true)}
      />
      {!canProduction && environment === 'production' && (
        <Callout tone="warn">Wywołania produkcyjne wymagają roli ADMIN lub OWNER.</Callout>
      )}
    </>
  );
}
